import { useEffect, useState } from 'react';
import { useTranslation } from '../i18n/useTranslation';
import { apiClient } from '../lib/apiClient';
import { Alert, LiveRegion, Text } from '../components/atoms';
import { AuthLayout } from '../components/templates/AuthLayout';
import './HealthPage.css';

interface HealthStatus {
  status: string;
  timestamp?: string;
  uptime?: number;
  version?: string;
  database?: string;
}

export function HealthPage() {
  const { t } = useTranslation();
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get<HealthStatus>('/api/v1/health')
      .then((res) => {
        setHealth(res);
        setLoading(false);
      })
      .catch(() => {
        // Never the raw backend message — always the translated copy.
        setError(t('health.fetchError'));
        setLoading(false);
      });
  }, [t]);

  const formatUptime = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${h}h ${m}m ${s}s`;
  };

  return (
    <AuthLayout title={t('health.pageTitle')} subtitle={t('health.description')}>
      <div className="health-page">
        {loading && (
          <LiveRegion className="health-page__loading">
            <Text className="health-status-message">{t('health.checking')}</Text>
          </LiveRegion>
        )}

        {error && (
          <Alert>{error}</Alert>
        )}

        {health && !loading && !error && (
          <div className="health-status-list">
            <div className="health-status-item">
              <Text className="health-status-term">{t('health.status')}</Text>
              <Text
                className={`health-status-value health-status-value--${health.status === 'ok' ? 'ok' : 'error'}`}
              >
                {health.status}
              </Text>
            </div>

            {health.timestamp && (
              <div className="health-status-item">
                <Text className="health-status-term">{t('health.timestamp')}</Text>
                <Text className="health-status-value">{health.timestamp}</Text>
              </div>
            )}

            {health.uptime !== undefined && (
              <div className="health-status-item">
                <Text className="health-status-term">{t('health.uptime')}</Text>
                <Text className="health-status-value">{formatUptime(health.uptime)}</Text>
              </div>
            )}

            {health.version && (
              <div className="health-status-item">
                <Text className="health-status-term">{t('health.version')}</Text>
                <Text className="health-status-value">{health.version}</Text>
              </div>
            )}

            {health.database && (
              <div className="health-status-item">
                <Text className="health-status-term">{t('health.database')}</Text>
                <Text className="health-status-value">{health.database}</Text>
              </div>
            )}
          </div>
        )}
      </div>
    </AuthLayout>
  );
}

export default HealthPage;
